if (!customElements.get('homepage-product-hero')) {
  customElements.define(
    'homepage-product-hero',
    class HomepageProductHero extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-type="add-to-cart-form"]');
        this.variantInput = this.querySelector('[name="id"]');
        this.sellingPlanInput = this.querySelector('[name="selling_plan"]');
        this.packButtons = Array.from(this.querySelectorAll('[data-pack]'));
        this.purchaseButtons = Array.from(this.querySelectorAll('[data-purchase]'));
        this.accordions = Array.from(this.querySelectorAll('[data-accordions] details'));
        this.submitButton = this.querySelector('[type="submit"]');
        this.trustBar = this.querySelector('[data-trust]');

        this.variants = this.readVariants();
        this.purchaseType = this.dataset.defaultPurchase || 'subscribe';
        this.selectedId = this.variantInput ? Number(this.variantInput.value) : null;
        this.accordionMs = 500;

        this.packButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectPack(Number(button.dataset.variantId)));
        });

        this.purchaseButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectPurchase(button.dataset.purchase));
        });

        this.frequencySelect = this.querySelector('[data-frequency]');
        this.subscribeCard = this.querySelector('[data-purchase-card="subscribe"]');
        this.selectedInterval = Number(this.frequencySelect?.value || 30);

        if (this.frequencySelect) {
          this.frequencySelect.addEventListener('change', () => {
            this.selectedInterval = Number(this.frequencySelect.value);
            this.selectPurchase('subscribe');
          });
          this.frequencySelect.addEventListener('click', (event) => event.stopPropagation());
        }

        this.subscribeCard?.addEventListener('click', (event) => {
          if (event.target.closest('[data-frequency], .homepage-product-hero__frequency')) return;
          this.selectPurchase('subscribe');
        });

        this.bindAccordions();
        this.bindGallery();
        this.bindZoom();
        this.bindTrustBar();
        this.bindSealWidgetGuard();
        this.updateUI();
      }

      disconnectedCallback() {
        this.closeZoom();
        this.zoomPopup?.remove();
        this.disableGallery();
        this.removeEventListener('pointerup', this.onZoomPointerUp);
        this.removeEventListener('keydown', this.onZoomKeyActivate);
        if (this.galleryMedia) {
          this.galleryMedia.removeEventListener('change', this.onGalleryMediaChange);
        }
        window.removeEventListener('resize', this.onTrustResize);
        window.removeEventListener('resize', this.onGalleryResize);
        this.trustObserver?.disconnect();
        this.sealObserver?.disconnect();
      }

      readVariants() {
        const script = this.querySelector('[data-variants-json]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return [];
        }
      }

      currentVariant() {
        return this.variants.find((variant) => variant.id === this.selectedId) || this.variants[0];
      }

      firstAvailableVariant() {
        return this.variants.find((variant) => variant.available) || null;
      }

      selectPack(variantId) {
        const next = this.variants.find((variant) => variant.id === variantId);
        if (!next?.available) return;
        this.selectedId = variantId;
        this.updateUI();
      }

      selectPurchase(type) {
        this.purchaseType = type;
        this.updateUI();
      }

      currentPlan() {
        const variant = this.currentVariant();
        if (!variant) return null;
        const plans = Array.isArray(variant.plans) ? variant.plans : [];
        return (
          plans.find((plan) => Number(plan.interval_days) === Number(this.selectedInterval) && plan.id) ||
          plans.find((plan) => plan.id) ||
          (variant.plan_id ? { id: variant.plan_id, price_formatted: variant.plan_price_formatted, price: variant.plan_price } : null)
        );
      }

      updateUI() {
        let variant = this.currentVariant();
        if (!variant) return;

        if (!variant.available) {
          const availableVariant = this.firstAvailableVariant();
          if (availableVariant) {
            this.selectedId = availableVariant.id;
            variant = availableVariant;
          }
        }

        const plan = this.currentPlan();
        const useSubscribe = this.purchaseType === 'subscribe';
        const planId = plan?.id || '';
        const subscribePrice = plan?.price_formatted || variant.plan_price_formatted;
        const subscribeCompareOffer = this.compareOffer(plan?.price ?? variant.plan_price, variant);

        if (this.variantInput) {
          this.variantInput.value = variant.id;
          this.variantInput.disabled = false;
          this.variantInput.removeAttribute('disabled');
        }

        if (this.sellingPlanInput) {
          this.sellingPlanInput.disabled = false;
          this.sellingPlanInput.removeAttribute('disabled');
          this.sellingPlanInput.value = useSubscribe && planId ? String(planId) : '0';
        }

        if (this.frequencySelect) {
          this.frequencySelect.value = String(this.selectedInterval);
          this.frequencySelect.disabled = !useSubscribe;
        }

        this.subscribeCard?.classList.toggle('is-selected', useSubscribe);

        this.packButtons.forEach((button) => {
          const packId = Number(button.dataset.variantId);
          const pack = this.variants.find((item) => item.id === packId);
          const isUnavailable = pack?.available === false;
          const selected = packId === variant.id;

          button.classList.toggle('is-selected', selected);
          button.classList.toggle('is-unavailable', isUnavailable);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          button.disabled = isUnavailable;

          if (isUnavailable) {
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('aria-disabled');
          }

          if (pack) this.updatePackOffer(button, pack, useSubscribe);
        });

        this.purchaseButtons.forEach((button) => {
          const selected = button.dataset.purchase === this.purchaseType;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        this.setPrice('[data-subscribe-price]', subscribePrice);
        this.setPrice('[data-subscribe-compare]', subscribeCompareOffer.formatted, subscribeCompareOffer.show);
        this.setPrice('[data-onetime-price]', variant.onetime_price_formatted);
        this.setPrice('[data-onetime-compare]', variant.onetime_compare_formatted, Boolean(variant.onetime_compare_formatted));

        if (this.submitButton) {
          const unavailable = !variant.available;
          const missingSubscribePlan = useSubscribe && !planId;
          const disableSubmit = unavailable || missingSubscribePlan;
          const isSubmitting =
            this.submitButton.classList.contains('loading') &&
            this.submitButton.getAttribute('aria-disabled') === 'true';

          this.submitButton.disabled = disableSubmit;

          if (disableSubmit) {
            this.submitButton.setAttribute('disabled', 'disabled');
          } else if (!isSubmitting) {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
            this.restoreSubmitAppearance();
          }
        }
      }

      bindSealWidgetGuard() {
        this.stripSealWidgets();
        if (!this.form || this.sealObserver) return;

        this.sealObserver = new MutationObserver(() => this.stripSealWidgets());
        this.sealObserver.observe(this.form, { childList: true, subtree: true });
      }

      stripSealWidgets() {
        if (!this.form) return;

        this.form
          .querySelectorAll(
            '.sealsubs-target-element, [data-seal-ac], [data-seal-in], [id^="sealsubs"], [class*="sealsubs"]'
          )
          .forEach((node) => node.remove());

        this.form.querySelectorAll('[name="selling_plan"]').forEach((input) => {
          if (input !== this.sellingPlanInput) input.remove();
        });

        this.restoreSubmitAppearance();
      }

      restoreSubmitAppearance() {
        const button = this.submitButton;
        if (!button) return;

        const isSubmitting =
          button.classList.contains('loading') && button.getAttribute('aria-disabled') === 'true';
        if (isSubmitting) return;

        button.classList.remove('loading');
        button.querySelector('.loading__spinner')?.classList.add('hidden');

        const text = (this.dataset.buttonLabel || button.dataset.label || '').trim();
        if (!text) return;

        let label = button.querySelector('[data-submit-label]');
        if (!label) {
          label = document.createElement('span');
          label.setAttribute('data-submit-label', '');
          button.prepend(label);
        }
        if (label.textContent.trim() !== text) {
          label.textContent = text;
        }
      }

      setPrice(selector, value, show = true) {
        this.querySelectorAll(selector).forEach((el) => {
          if (!value || !show) {
            el.hidden = true;
            return;
          }
          el.hidden = false;
          el.textContent = value;
        });
      }

      setText(el, value, show = true) {
        if (!el) return;
        if (!value || !show) {
          el.hidden = true;
          el.textContent = '';
          return;
        }
        el.hidden = false;
        el.textContent = value;
      }

      planFor(pack) {
        const plans = Array.isArray(pack?.plans) ? pack.plans : [];
        return (
          plans.find((plan) => Number(plan.interval_days) === Number(this.selectedInterval) && plan.id) ||
          plans.find((plan) => plan.id) ||
          {
            id: pack.plan_id,
            price: pack.plan_price,
            price_formatted: pack.plan_price_formatted,
          }
        );
      }

      compareOffer(displayPrice, pack) {
        const price = Number(displayPrice) || 0;
        const compareAt = Number(pack?.compare_at) || 0;
        const oneTime = Number(pack?.price) || 0;

        if (compareAt > price) {
          return { amount: compareAt, formatted: pack.compare_at_formatted, show: true };
        }
        if (oneTime > price) {
          return { amount: oneTime, formatted: pack.price_formatted, show: true };
        }
        return { amount: 0, formatted: '', show: false };
      }

      saveBadge(price, compare, withShipping) {
        const amount = Number(price) || 0;
        const compared = Number(compare) || 0;
        if (compared <= amount) return '';

        const percent = Math.round(((compared - amount) / compared) * 100);
        if (percent <= 0) return '';

        const suffix = (this.dataset.freeShippingSuffix || '').trim();
        if (withShipping && suffix) return `Save ${percent}% ${suffix}`;
        return `Save ${percent}%`;
      }

      updatePackOffer(button, pack, useSubscribe) {
        const plan = this.planFor(pack);
        const price = useSubscribe ? Number(plan?.price ?? pack.plan_price) : Number(pack.price);
        const priceFormatted = useSubscribe
          ? plan?.price_formatted || pack.plan_price_formatted
          : pack.price_formatted || pack.onetime_price_formatted;
        const compare = this.compareOffer(price, pack);
        const withShipping = useSubscribe ? Boolean(pack.shipping_subscribe) : Boolean(pack.shipping_onetime);

        this.setText(button.querySelector('[data-pack-price]'), priceFormatted);
        this.setText(button.querySelector('[data-pack-compare]'), compare.formatted, compare.show);
        this.setText(button.querySelector('[data-pack-save]'), this.saveBadge(price, compare.amount, withShipping));
      }

      bindAccordions() {
        this.accordions.forEach((details) => {
          const body = details.querySelector('.homepage-product-hero__accordion-body');
          const summary = details.querySelector('summary');
          if (!body || !summary) return;

          body.style.height = details.open ? `${body.scrollHeight}px` : '0px';

          summary.addEventListener('click', (event) => {
            event.preventDefault();
            if (details.open) {
              this.closeAccordion(details);
              return;
            }
            this.accordions.forEach((other) => {
              if (other !== details && other.open) this.closeAccordion(other);
            });
            this.openAccordion(details);
          });
        });
      }

      openAccordion(details) {
        const body = details.querySelector('.homepage-product-hero__accordion-body');
        if (!body) return;
        details.setAttribute('open', '');
        body.style.height = '0px';
        requestAnimationFrame(() => {
          body.style.height = `${body.scrollHeight}px`;
        });
        const onEnd = (event) => {
          if (event.propertyName !== 'height') return;
          body.style.height = 'auto';
          body.removeEventListener('transitionend', onEnd);
        };
        body.addEventListener('transitionend', onEnd);
      }

      closeAccordion(details) {
        const body = details.querySelector('.homepage-product-hero__accordion-body');
        if (!body) return;
        body.style.height = `${body.scrollHeight}px`;
        requestAnimationFrame(() => {
          body.style.height = '0px';
        });
        const onEnd = (event) => {
          if (event.propertyName !== 'height') return;
          details.removeAttribute('open');
          body.removeEventListener('transitionend', onEnd);
        };
        body.addEventListener('transitionend', onEnd);
      }

      bindTrustBar() {
        this.onTrustResize = () => this.fitTrustBar();
        window.addEventListener('resize', this.onTrustResize);
        this.trustObserver = new ResizeObserver((entries) => {
          const width = Math.round(entries[0]?.contentRect?.width || 0);
          if (width === this.lastTrustWidth) return;
          this.lastTrustWidth = width;
          this.fitTrustBar();
        });
        this.trustObserver.observe(this.trustBar || this);
        this.fitTrustBar();
        if (document.fonts?.ready) {
          document.fonts.ready.then(() => this.fitTrustBar());
        }
      }

      trustBarOverflows(bar) {
        const styles = getComputedStyle(bar);
        const horizontalPad = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        const items = Array.from(bar.children);
        const itemsWidth = items.reduce((sum, item) => sum + item.scrollWidth, 0);
        const gapsWidth = gap * Math.max(0, items.length - 1);
        return itemsWidth + gapsWidth + horizontalPad > bar.clientWidth + 0.5;
      }

      fitTrustBar() {
        const bar = this.trustBar;
        if (!bar) return;

        if (window.innerWidth > 749) {
          bar.style.removeProperty('--trust-size');
          return;
        }

        const phpPx = parseFloat(getComputedStyle(this).getPropertyValue('--php-px')) || window.innerWidth / 430;
        let size = 9 * phpPx;
        const minSize = 5;

        bar.style.setProperty('--trust-size', `${size}px`);

        while (size > minSize && this.trustBarOverflows(bar)) {
          size -= 0.2;
          bar.style.setProperty('--trust-size', `${size}px`);
        }
      }

      bindGallery() {
        this.slider = this.querySelector('[data-gallery-slider]');
        if (!this.slider) return;

        this.slides = Array.from(this.slider.querySelectorAll('[data-slide]'));
        this.realCount = this.slides.filter((slide) => !slide.hasAttribute('data-clone')).length;
        this.looping = this.realCount > 0 && this.slides.length > this.realCount;
        this.domIndex = this.looping ? 1 : 0;
        this.galleryMs = 420;
        this.slider.style.scrollBehavior = 'auto';

        this.preloadGalleryImages();

        this.onGalleryMediaChange = this.onGalleryMediaChange.bind(this);
        this.onGalleryPointerDown = this.onGalleryPointerDown.bind(this);
        this.onGalleryPointerMove = this.onGalleryPointerMove.bind(this);
        this.onGalleryPointerUp = this.onGalleryPointerUp.bind(this);
        this.onGalleryResize = this.onGalleryResize.bind(this);

        this.galleryMedia = window.matchMedia('(max-width: 749px)');
        this.galleryMedia.addEventListener('change', this.onGalleryMediaChange);
        window.addEventListener('resize', this.onGalleryResize);
        this.onGalleryMediaChange();
      }

      preloadGalleryImages() {
        this.slider?.querySelectorAll('img').forEach((img) => {
          img.loading = 'eager';
          img.removeAttribute('loading');
          if (typeof img.decode === 'function') img.decode().catch(() => {});
        });
      }

      onGalleryMediaChange() {
        if (!this.slider) return;
        if (this.galleryMedia.matches) {
          this.enableGallery();
          requestAnimationFrame(() => this.jumpTo(this.domIndex));
        } else {
          this.disableGallery();
        }
      }

      onGalleryResize() {
        if (!this.slider || !this.galleryMedia?.matches) return;
        this.jumpTo(this.domIndex);
        this.fitTrustBar();
      }

      enableGallery() {
        if (this.galleryEnabled || !this.slider) return;
        this.galleryEnabled = true;
        this.slider.addEventListener('pointerdown', this.onGalleryPointerDown);
      }

      disableGallery() {
        if (!this.slider) return;
        this.galleryEnabled = false;
        this.slider.removeEventListener('pointerdown', this.onGalleryPointerDown);
        this.slider.removeEventListener('pointermove', this.onGalleryPointerMove);
        this.slider.removeEventListener('pointerup', this.onGalleryPointerUp);
        this.slider.removeEventListener('pointercancel', this.onGalleryPointerUp);
        this.cancelGallerySettle();
      }

      slideScrollLeft(domIndex) {
        const slide = this.slides[domIndex];
        if (!slide) return 0;
        const sliderRect = this.slider.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        return this.slider.scrollLeft + (slideRect.left - sliderRect.left) - (sliderRect.width - slideRect.width) / 2;
      }

      jumpTo(domIndex) {
        this.cancelGallerySettle();
        this.domIndex = Math.max(0, Math.min(domIndex, this.slides.length - 1));
        this.slider.scrollLeft = this.slideScrollLeft(this.domIndex);
      }

      syncLoop() {
        if (!this.looping || this.realCount < 1) return;
        if (this.domIndex === 0) {
          this.jumpTo(this.realCount);
          return;
        }
        if (this.domIndex >= this.realCount + 1) {
          this.jumpTo(1);
        }
      }

      goToNeighbor(direction) {
        this.syncLoop();
        const next = this.domIndex + direction;
        if (next < 0 || next >= this.slides.length) return;
        this.animateGalleryTo(next, () => this.syncLoop());
      }

      animateGalleryTo(domIndex, after) {
        const targetIndex = Math.max(0, Math.min(domIndex, this.slides.length - 1));
        this.cancelGallerySettle();
        const start = this.slider.scrollLeft;
        const target = this.slideScrollLeft(targetIndex);
        const distance = target - start;
        this.domIndex = targetIndex;

        if (Math.abs(distance) < 1) {
          this.syncLoop();
          after?.();
          return;
        }

        const startTime = performance.now();
        this.gallerySettling = true;

        const step = (now) => {
          const t = Math.min(1, (now - startTime) / this.galleryMs);
          this.slider.scrollLeft = start + distance * this.easeOutCubic(t);
          if (t < 1) {
            this.galleryFrame = requestAnimationFrame(step);
            return;
          }
          this.slider.scrollLeft = this.slideScrollLeft(this.domIndex);
          this.gallerySettling = false;
          this.galleryFrame = null;
          after?.();
        };

        this.galleryFrame = requestAnimationFrame(step);
      }

      cancelGallerySettle() {
        if (this.galleryFrame) {
          cancelAnimationFrame(this.galleryFrame);
          this.galleryFrame = null;
        }
        this.gallerySettling = false;
      }

      easeOutCubic(t) {
        return 1 - (1 - t) ** 3;
      }

      onGalleryPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        this.galleryTracking = true;
        this.galleryDragging = false;
        this.galleryWasDragging = false;
        this.galleryStartX = event.clientX;
        this.galleryLastX = event.clientX;
        this.galleryStartScroll = this.slider.scrollLeft;
        this.cancelGallerySettle();
        this.syncLoop();
        this.galleryStartScroll = this.slider.scrollLeft;

        this.slider.addEventListener('pointermove', this.onGalleryPointerMove);
        this.slider.addEventListener('pointerup', this.onGalleryPointerUp);
        this.slider.addEventListener('pointercancel', this.onGalleryPointerUp);
      }

      onGalleryPointerMove(event) {
        if (!this.galleryTracking) return;
        this.galleryLastX = event.clientX;
        const delta = event.clientX - this.galleryStartX;

        if (!this.galleryDragging) {
          if (Math.abs(delta) < 10) return;
          this.galleryDragging = true;
          this.slider.classList.add('is-dragging');
          try {
            this.slider.setPointerCapture(event.pointerId);
          } catch (error) {
            /* ignore */
          }
        }

        event.preventDefault();
        this.slider.scrollLeft = this.galleryStartScroll - delta;
      }

      onGalleryPointerUp() {
        if (!this.galleryTracking) return;
        this.galleryTracking = false;
        this.slider.removeEventListener('pointermove', this.onGalleryPointerMove);
        this.slider.removeEventListener('pointerup', this.onGalleryPointerUp);
        this.slider.removeEventListener('pointercancel', this.onGalleryPointerUp);
        this.slider.classList.remove('is-dragging');

        if (!this.galleryDragging) return;
        this.galleryWasDragging = true;
        this.galleryDragging = false;

        const delta = this.galleryStartX - (this.galleryLastX ?? this.galleryStartX);
        const threshold = Math.max(36, (this.slides[this.domIndex]?.offsetWidth || 200) * 0.14);
        if (delta > threshold) {
          this.goToNeighbor(1);
        } else if (delta < -threshold) {
          this.goToNeighbor(-1);
        } else {
          this.animateGalleryTo(this.domIndex);
        }
      }

      bindZoom() {
        this.zoomPopup =
          this.closest('.shopify-section')?.querySelector('[data-zoom-popup]') || this.querySelector('[data-zoom-popup]');
        this.zoomImage = this.zoomPopup?.querySelector('[data-zoom-image]');
        this.onZoomKeydown = this.onZoomKeydown.bind(this);
        this.onZoomPointerUp = this.onZoomPointerUp.bind(this);
        this.onZoomKeyActivate = this.onZoomKeyActivate.bind(this);

        if (this.zoomPopup && this.zoomPopup.parentElement !== document.body) {
          document.body.appendChild(this.zoomPopup);
        }

        this.addEventListener('pointerup', this.onZoomPointerUp);
        this.addEventListener('keydown', this.onZoomKeyActivate);

        this.zoomPopup?.addEventListener('click', (event) => {
          if (event.target.closest('.homepage-product-hero__popup-image')) return;
          this.closeZoom();
        });
      }

      onZoomPointerUp(event) {
        const trigger = event.target.closest('[data-zoom-src]');
        if (!trigger || !this.contains(trigger)) return;
        if (this.galleryDragging || this.galleryWasDragging) return;
        event.preventDefault();
        event.stopPropagation();
        this.openZoom(trigger);
      }

      onZoomKeyActivate(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const trigger = event.target.closest('[data-zoom-src]');
        if (!trigger || event.target !== trigger) return;
        event.preventDefault();
        this.openZoom(trigger);
      }

      openZoom(trigger) {
        if (!this.zoomPopup || !this.zoomImage || !trigger) return;

        const media = trigger.closest(
          '.homepage-product-hero__slide, .homepage-product-hero__hero-media, .homepage-product-hero__tile'
        );
        const img = media?.querySelector('img');
        const src = img?.currentSrc || img?.src || trigger.dataset.zoomSrc;
        if (!src) return;

        this.zoomImage.removeAttribute('srcset');
        this.zoomImage.removeAttribute('sizes');
        this.zoomImage.alt = img?.alt || '';
        this.zoomImage.src = src;

        this.zoomPopup.hidden = false;
        this.zoomPopup.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onZoomKeydown);
        this.zoomPopup.querySelector('.homepage-product-hero__popup-close')?.focus();
      }

      closeZoom() {
        if (!this.zoomPopup) return;

        this.zoomPopup.classList.remove('is-open');
        this.zoomPopup.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onZoomKeydown);
      }

      onZoomKeydown(event) {
        if (event.key === 'Escape') this.closeZoom();
      }
    }
  );
}
